<template>
  <div v-if="mapUnavailable" class="listing-map-placeholder">
    <SvgIcon name="map" :size="32" />
    <p class="listing-map-placeholder__label">{{ t('listing-map.unavailable') }}</p>
  </div>
  <div
    v-else
    class="listing-map"
    :class="{ 'has-card': hasSelection, 'listing-map--narrow': narrowMap, 'listing-map--fullscreen': fullscreen }"
    :style="cardStyle"
  >
    <div ref="container" class="listing-map__canvas" />
    <div class="listing-map-legend" :class="{ 'is-open': legendOpen }">
      <button
        type="button"
        class="listing-map-legend__toggle"
        :aria-label="legendOpen ? t('listing-map.legend.hide') : t('listing-map.legend.show')"
        :aria-expanded="legendOpen"
        @click="legendOpen = !legendOpen"
      >
        <SvgIcon name="list" :size="16" class="listing-map-legend__icon" />
        {{ t('listing-map.legend.title') }}
        <SvgIcon name="chevron" :size="13" class="listing-map-legend__chevron" />
      </button>
      <div v-if="legendOpen" class="listing-map-legend__rows">
        <span class="listing-map-legend__row">
          <svg class="listing-map-legend__pin" :viewBox="PIN_VIEWBOX" aria-hidden="true">
            <g transform="translate(0.300671, 0.128358)">
              <path :d="PIN_BODY_PATH" :fill="MARKER_COLOR" />
              <g transform="translate(17, 22)">
                <path :d="PIN_W_PATH" fill="#ffffff" />
              </g>
            </g>
          </svg>
          {{ t('projects-map.legend.location') }}
        </span>
        <span class="listing-map-legend__row">
          <svg class="listing-map-legend__cluster" viewBox="0 0 72 72" aria-hidden="true">
            <circle cx="36" cy="38" r="25" :fill="MARKER_SHADOW" />
            <circle cx="36" cy="36" r="24" :fill="CLUSTER_FILL" :stroke="CLUSTER_STROKE" stroke-width="3" />
            <text x="36" y="42" text-anchor="middle" :fill="CLUSTER_STROKE" font-size="20" font-weight="700">4</text>
          </svg>
          {{ t('projects-map.legend.cluster') }}
        </span>
      </div>
    </div>

    <button
      type="button"
      class="listing-map-fullscreen"
      :aria-label="fullscreen ? t('listing-map.fullscreen.exit') : t('listing-map.fullscreen.enter')"
      :aria-pressed="fullscreen"
      @click="toggleFullscreen"
    >
      <SvgIcon :name="fullscreen ? 'collapse' : 'expand'" :size="18" :stroke="2" />
    </button>

    <MapCard
      v-if="hasSelection"
      ref="card"
      :index="selection.index"
      :total="selection.items.length"
      :label="t('projects-map.card.aria-label')"
      @prev="step(-1)"
      @next="step(1)"
      @close="onCardClose"
      @resize="onCardResize"
    >
      <ProjectMapItem
        :key="currentItem?.id ?? 'loading'"
        :project="selectionDetail.project"
        :error="selectionDetail.error"
        @select="openSelectedProject"
      />
      <template #cta>
        <button
          v-if="selectionDetail.project"
          type="button"
          class="listing-map-card__cta"
          @click="openSelectedProject(selectionDetail.project)"
        >
          {{ t('projects-map.card.cta') }}
        </button>
      </template>
    </MapCard>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import MapCard from './MapCard.vue';
import ProjectMapItem from './ProjectMapItem.vue';
import SvgIcon from './SvgIcon.vue';
import { circleBounds, circlePolygon } from '../lib/geoCircle';
import { t } from '../lib/i18n';
import {
  MARKER_COLOR,
  MARKER_IMAGE_ID,
  PIN_BODY_PATH,
  PIN_VIEWBOX,
  PIN_W_PATH,
  SELECTED_MARKER_IMAGE_ID,
  SELECTED_PIN_OFFSET,
  addMarkerImages
} from '../lib/mapPin';
import { CLUSTER_TOOLTIP_OFFSET, PIN_TOOLTIP_OFFSET, createMapTooltip, tooltipContent } from '../lib/mapTooltip';
import { createProjectsApi } from '../lib/projectsApi';
import { useProjectCriteria } from '../lib/useProjectCriteria';
import { useProjectDetail } from '../lib/useProjectDetail';
import { useMapFullscreen } from '../lib/useMapFullscreen';
import { useMapState } from '../lib/useMapState';
import { useResults } from '../lib/useResults';
import { useSelectionCard } from '../lib/useSelectionCard';

// Same work-area bias as ListingMap (Brabant/Gelderland)
const INITIAL_CENTER = [5.6, 51.95];
const INITIAL_ZOOM = 7.1;
const MAX_ZOOM = 13;

const PROJECTS_SOURCE = 'projects';
const RADIUS_SOURCE = 'search-radius';
const SELECTED_LAYER = 'project-selected';
// Inverted cluster: white body with a red edge, so shape and fill both separate
// "one place" from "a group of places".
const CLUSTER_FILL = '#ffffff';
const CLUSTER_STROKE = MARKER_COLOR;
// The map style is light, so a white outline barely registers; a dark shadow
// under the marker is what actually lifts it off the background. Kept as color
// plus opacity because the pin SVG is rasterised through an Image, where the
// SVG-native stroke-opacity is safer than an rgba() color value.
const SHADOW_COLOR = '#000000';
const SHADOW_OPACITY = 0.35;
const MARKER_SHADOW = `rgba(0, 0, 0, ${SHADOW_OPACITY})`;
// The search radius keeps its own blue: it is the area the user set, not a
// marker, and it must stay distinguishable from them.
const RADIUS_COLOR = '#008cba';
const RADIUS_FIT_PADDING = 40;
const MARKER_FIT_PADDING = 48;
// Well below MAX_ZOOM: re-framing after "Filters wissen" should show the spread
// of the results, not dive into one of them.
const MARKER_FIT_MAX_ZOOM = 11;
const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };

// Funda-rule for clusters: a handful of markers that only separate at street
// level is faster to browse in the card than to zoom into. Tuned with the PO
// on the live data.
const SHALLOW_CLUSTER = { maxMarkers: 4, minExpansionZoom: 11 };
// maplibre's default is 10 leaves, which would silently truncate the card
const CLUSTER_LEAF_LIMIT = 100;
// Below this container width the card spans the map and covers the zoom
// controls, which then move up above it. Wider maps leave room beside the card
// and keep the controls where they are, so hovering pins does not bounce them.
const NARROW_MAP_WIDTH = 480;

const criteriaStore = useProjectCriteria();
const { criteria, update } = criteriaStore;
const { results } = useResults();
const { bbox, setBbox, fitRequest } = useMapState();
const { open: openDetail, state: detailState } = useProjectDetail();

const api = createProjectsApi();

// maplibre-gl comes from the CDN include in overview.html.twig
const mapUnavailable = ref(false);

const container = ref(null);

const legendOpen = ref(false);

let map = null;
let resizeObserver = null;
let centerMarker = null;
let radiusLabel = null;
const tooltip = createMapTooltip(() => map);

const narrowMap = ref(false);

// The selected entries are {id, locationKey} per project (phase): one pin can
// hold several phases and a cluster several pins, and the card browses them
// one by one, so the entries are the items.
const {
  selection,
  hasSelection,
  currentItem,
  card,
  cardStyle,
  openSelection,
  closeSelection,
  onCardClose,
  step,
  onCardResize,
  prefetchNeighbours,
  centerOnMarker,
  revealClickedPin
} = useSelectionCard({
  container,
  detailState,
  getMap: () => map,
  withProgrammaticMove: (fn) => withProgrammaticMove(fn)
});

const { fullscreen, toggleFullscreen } = useMapFullscreen({
  getMap: () => map,
  canClose: () => !hasSelection.value && !detailState.isOpen
});

const selectionDetail = reactive({ project: null, error: false });

const openSelectedProject = (project) => openDetail(project);

// The marker payload only carries {id, long, lat, status}; names, housing
// counts and the city come from the project detail endpoint, fetched lazily
// per item and cached in projectsApi.
let detailRequest = 0;

watch(currentItem, async (item) => {
  detailRequest += 1;
  const token = detailRequest;
  selectionDetail.project = null;
  selectionDetail.error = false;
  if (!item) return;

  try {
    const project = await api.detail(item.id);
    if (token !== detailRequest) return;
    selectionDetail.project = project;
    prefetchNeighbours((next) => api.detail(next.id));
  } catch {
    if (token !== detailRequest) return;
    selectionDetail.error = true;
  }
});

// The rim on the selected pin follows the current item, so browsing a
// cluster's items on the card also shows which pin you are looking at.
const syncSelectedPin = () => {
  if (!map || !map.getLayer(SELECTED_LAYER)) return;
  map.setFilter(SELECTED_LAYER, [
    'all',
    ['!', ['has', 'point_count']],
    ['==', ['get', 'locationKey'], currentItem.value?.locationKey ?? '']
  ]);
};

watch(currentItem, syncSelectedPin);

const hasLocation = () => criteria.long !== null && criteria.lat !== null;

// See ListingMap: every programmatic camera move raises this flag so moveend
// only writes a bbox for real user gestures.
let programmaticMove = false;

// Raised by a re-frame request from outside (WHN-2127) and consumed once the
// matching result set has landed.
let pendingFit = false;

const withProgrammaticMove = (fn) => {
  programmaticMove = true;
  fn();
};

const onMoveEnd = () => {
  if (programmaticMove) {
    programmaticMove = false;
    return;
  }
  const bounds = map.getBounds();
  setBbox([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
};

// Subprojects share their parent project's centerpoint, so identical
// coordinates are grouped into one location feature up front: one pin with the
// project count, whose card browses every project on that spot. MapLibre's own
// clustering then only merges *different* locations, drawn as the inverted
// cluster circle.
const toGeoJson = (markers) => {
  const locations = new Map();
  markers
    .filter((marker) => marker.long !== null && marker.lat !== null)
    .forEach((marker) => {
      const key = `${marker.long},${marker.lat}`;
      if (!locations.has(key)) locations.set(key, { long: marker.long, lat: marker.lat, ids: [] });
      locations.get(key).ids.push(marker.id);
    });

  return {
    type: 'FeatureCollection',
    features: [...locations.values()].map((location) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [location.long, location.lat] },
      properties: {
        locationKey: `${location.long},${location.lat}`,
        ids: JSON.stringify(location.ids),
        count: location.ids.length
      }
    }))
  };
};

// One selection item per project on a location feature; cluster leaves are
// location features too, so a cluster's items are simply all its leaves' items
const locationItems = (feature) => {
  const { locationKey } = feature.properties;
  return JSON.parse(feature.properties.ids).map((id) => ({ id, locationKey }));
};

const addRadiusLayers = () => {
  map.addSource(RADIUS_SOURCE, { type: 'geojson', data: EMPTY_COLLECTION });
  map.addLayer({
    id: 'search-radius-fill',
    type: 'fill',
    source: RADIUS_SOURCE,
    paint: { 'fill-color': RADIUS_COLOR, 'fill-opacity': 0.08 }
  });
  map.addLayer({
    id: 'search-radius-line',
    type: 'line',
    source: RADIUS_SOURCE,
    paint: { 'line-color': RADIUS_COLOR, 'line-width': 2 }
  });
};

const updateRadiusCircle = () => {
  const source = map.getSource(RADIUS_SOURCE);
  if (!source) return;

  if (centerMarker) {
    centerMarker.remove();
    centerMarker = null;
  }
  if (radiusLabel) {
    radiusLabel.remove();
    radiusLabel = null;
  }

  if (!hasLocation() || criteria.radius === null) {
    source.setData(EMPTY_COLLECTION);
    return;
  }

  source.setData({
    type: 'Feature',
    geometry: circlePolygon(criteria.long, criteria.lat, criteria.radius),
    properties: {}
  });

  const element = document.createElement('div');
  element.className = 'marker-search-location';
  centerMarker = new window.maplibregl.Marker({ element, anchor: 'center' })
    .setLngLat([criteria.long, criteria.lat])
    .addTo(map);

  const labelElement = document.createElement('div');
  labelElement.className = 'listing-map-radius-label';
  const labelText = document.createElement('span');
  labelText.textContent = criteria.loc
    ? `${criteria.radius} km · ${criteria.loc}`
    : `${criteria.radius} km`;
  const labelClear = document.createElement('button');
  labelClear.type = 'button';
  labelClear.className = 'listing-map-radius-label__clear';
  labelClear.setAttribute('aria-label', t('listing-map.radius-clear'));
  labelClear.textContent = '×';
  labelClear.addEventListener('click', (event) => {
    event.stopPropagation();
    update({ radius: null });
  });
  labelElement.append(labelText, labelClear);
  const topLatitude = circleBounds(criteria.long, criteria.lat, criteria.radius)[3];
  radiusLabel = new window.maplibregl.Marker({ element: labelElement, anchor: 'bottom', offset: [0, -6] })
    .setLngLat([criteria.long, topLatitude])
    .addTo(map);
};

const fitRadius = (animate) =>
  withProgrammaticMove(() =>
    map.fitBounds(circleBounds(criteria.long, criteria.lat, criteria.radius), {
      padding: RADIUS_FIT_PADDING,
      animate
    })
  );

const hasRadius = () => hasLocation() && criteria.radius !== null;

// Frames every marker of the current result set. maxZoom keeps a single result
// from slamming the camera to street level; the fit runs behind the programmatic
// flag so re-framing never writes a bbox and re-filters the list it just freed.
const fitToMarkers = () => {
  const positioned = results.markers.filter(
    (marker) => marker.long !== null && marker.lat !== null
  );
  if (!positioned.length) return;

  const bounds = positioned.reduce(
    (extent, marker) => extent.extend([marker.long, marker.lat]),
    new window.maplibregl.LngLatBounds(
      [positioned[0].long, positioned[0].lat],
      [positioned[0].long, positioned[0].lat]
    )
  );

  withProgrammaticMove(() =>
    map.fitBounds(bounds, { padding: MARKER_FIT_PADDING, maxZoom: MARKER_FIT_MAX_ZOOM })
  );
};

// An active radius is the search area the user drew, so it has to stay fully in
// view; the marker extent would zoom past it, because every result lies inside
// the circle by definition (the radius filters server-side).
const reframe = () => (hasRadius() ? fitRadius(true) : fitToMarkers());

const addProjectLayers = () => {
  map.addSource(PROJECTS_SOURCE, {
    type: 'geojson',
    data: toGeoJson(results.markers),
    cluster: true,
    clusterRadius: 35,
    // Clusters sum the projects of every merged location, so the number keeps
    // meaning "projects" at every zoom level
    clusterProperties: { projectCount: ['+', ['get', 'count']] }
  });
  // Shadow under the cluster: the previous translucent halo in the marker color
  // is now a dark offset circle, because on this light map style that is what
  // makes the white cluster body read as raised (WHN-2132).
  map.addLayer({
    id: 'project-cluster-shadow',
    type: 'circle',
    source: PROJECTS_SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': MARKER_SHADOW,
      'circle-translate': [0, 1],
      'circle-radius': ['step', ['get', 'projectCount'], 17, 10, 21, 25, 27]
    }
  });
  map.addLayer({
    id: 'project-clusters',
    type: 'circle',
    source: PROJECTS_SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': CLUSTER_FILL,
      'circle-stroke-color': CLUSTER_STROKE,
      'circle-stroke-width': 2,
      'circle-radius': ['step', ['get', 'projectCount'], 16, 10, 20, 25, 26]
    }
  });
  map.addLayer({
    id: 'project-cluster-counts',
    type: 'symbol',
    source: PROJECTS_SOURCE,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['to-string', ['get', 'projectCount']],
      // Bold is available in the OpenFreeMap glyph set, checked against
      // /fonts/Noto%20Sans%20Bold/0-255.pbf
      'text-font': ['Noto Sans Bold'],
      'text-size': 12,
      'text-allow-overlap': true
    },
    paint: { 'text-color': CLUSTER_STROKE }
  });
  map.addLayer({
    id: 'project-points',
    type: 'symbol',
    source: PROJECTS_SOURCE,
    filter: ['!', ['has', 'point_count']],
    layout: {
      'icon-image': MARKER_IMAGE_ID,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true
      // No count in the pin: the W fills the head. One marker for one project,
      // whether it holds one phase or several (WHN-2132); how many there are is
      // what the card answers. Clusters still carry their own count, because
      // those group different locations.
    },
    paint: { 'text-color': '#fff' }
  });
  // The selected pin, drawn over the plain one so the rim shows; the filter is
  // set from the selection and matches nothing until a pin is selected
  map.addLayer({
    id: SELECTED_LAYER,
    type: 'symbol',
    source: PROJECTS_SOURCE,
    filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'locationKey'], '']],
    layout: {
      'icon-image': SELECTED_MARKER_IMAGE_ID,
      'icon-anchor': 'bottom',
      'icon-offset': [0, SELECTED_PIN_OFFSET],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true
    }
  });
  syncSelectedPin();

  // Three outcomes for a cluster click. A small cluster that only splits at
  // street level opens the card straight away (Funda-rule), a cluster that
  // still separates on zoom zooms as before, and a cluster that never
  // separates within MAX_ZOOM opens the card too: without that the click did
  // nothing, because easeTo clamped the expansion zoom to MAX_ZOOM.
  map.on('click', 'project-clusters', async (event) => {
    const feature = event.features[0];
    const { cluster_id: clusterId, point_count: markerCount } = feature.properties;
    tooltip.hide();
    const source = map.getSource(PROJECTS_SOURCE);
    const zoom = await source.getClusterExpansionZoom(clusterId);
    const isShallow =
      markerCount <= SHALLOW_CLUSTER.maxMarkers && zoom > SHALLOW_CLUSTER.minExpansionZoom;

    // Not wrapped in withProgrammaticMove: zooming into a cluster is a user
    // gesture, so the camera move must update the bbox and the listing. The
    // card branch below only centers, flagged programmatic (see centerOnMarker)
    if (!isShallow && zoom <= MAX_ZOOM) {
      map.easeTo({ center: feature.geometry.coordinates, zoom });
      return;
    }

    const leaves = await source.getClusterLeaves(clusterId, CLUSTER_LEAF_LIMIT, 0);
    openSelection(leaves.flatMap(locationItems));
    centerOnMarker(feature.geometry.coordinates);
  });

  // mousemove instead of mouseenter: the layer stays "entered" when the cursor
  // slides from one marker straight onto a neighbouring one
  map.on('mousemove', 'project-clusters', (event) => {
    const feature = event.features[0];
    tooltip.show(
      `cluster:${feature.properties.cluster_id}`,
      feature.geometry.coordinates,
      tooltipContent({
        title: t('projects-map.cluster.summary', { count: feature.properties.projectCount }),
        hint: t('listing-map.cluster.zoom')
      }),
      CLUSTER_TOOLTIP_OFFSET
    );
  });
  map.on('mouseleave', 'project-clusters', () => tooltip.hide('cluster:'));

  // The selected pin gets no tooltip: the card already shows it
  map.on('mousemove', 'project-points', (event) => {
    const feature = event.features[0];
    if (currentItem.value?.locationKey === feature.properties.locationKey) {
      tooltip.hide();
      return;
    }
    tooltip.show(
      `pin:${feature.properties.locationKey}`,
      feature.geometry.coordinates,
      pinTooltipContent(feature.properties),
      PIN_TOOLTIP_OFFSET
    );
  });
  map.on('mouseleave', 'project-points', () => tooltip.hide('pin:'));

  map.on('click', 'project-points', (event) => {
    const feature = event.features[0];
    tooltip.hide();
    openSelection(locationItems(feature));
    revealClickedPin(feature.geometry.coordinates, event.point);
  });

  map.on('click', (event) => {
    if (!hasSelection.value) return;
    const hits = map.queryRenderedFeatures(event.point, { layers: ['project-points'] });
    if (!hits.length) closeSelection();
  });

  ['project-points', 'project-clusters'].forEach((layerId) => {
    map.on('mouseenter', layerId, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', layerId, () => {
      map.getCanvas().style.cursor = '';
    });
  });
};

const pinTooltipContent = ({ count }) =>
  tooltipContent({
    title: count === 1 ? t('projects-map.popup.project-one') : t('projects-map.cluster.summary', { count }),
    hint: t('listing-map.pin.hint')
  });

const collapseAttribution = () => {
  container.value
    .querySelectorAll('.maplibregl-ctrl-attrib.maplibregl-compact-show')
    .forEach((attribution) => {
      attribution.setAttribute('open', '');
      attribution.classList.remove('maplibregl-compact-show');
    });
};

const applyDutchLabels = () => {
  map.getStyle().layers.forEach((layer) => {
    if (!layer.layout || !layer.layout['text-field']) return;
    if (JSON.stringify(layer.layout['text-field']).includes('"name"')) {
      map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', 'name:nl'], ['get', 'name']]);
    }
  });
};

onMounted(() => {
  if (!window.maplibregl) {
    mapUnavailable.value = true;
    return;
  }

  map = new window.maplibregl.Map({
    container: container.value,
    style: 'https://tiles.openfreemap.org/styles/bright',
    center: INITIAL_CENTER,
    zoom: INITIAL_ZOOM,
    maxZoom: MAX_ZOOM,
    attributionControl: { compact: true },
    // maplibre's own ResizeObserver would call resize() outside the
    // programmaticMove flag below, and that moveend writes a bbox: a search
    // request on every header or column resize. The ResizeObserver in
    // onMounted does the resizing, flagged.
    trackResize: false,
    cooperativeGestures: true,
    dragRotate: false,
    touchPitch: false,
    locale: {
      'CooperativeGesturesHandler.WindowsHelpText': t('listing-map.zoom-hint'),
      'CooperativeGesturesHandler.MacHelpText': t('listing-map.zoom-hint-mac'),
      'CooperativeGesturesHandler.MobileHelpText': t('listing-map.zoom-hint-mobile')
    }
  });
  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();
  map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
  map.scrollZoom.setWheelZoomRate(1 / 75);
  map.scrollZoom.setZoomRate(1 / 25);
  map.on('load', async () => {
    applyDutchLabels();
    addRadiusLayers();
    await addMarkerImages(() => map);
    if (!map) return;
    addProjectLayers();
    updateRadiusCircle();
    collapseAttribution();
    // Registered after load: maplibre fires a moveend while settling the
    // initial camera, which must not register as a user pan and write a bbox
    // (that silently drops projects without coordinates from the list).
    map.on('moveend', onMoveEnd);
  });
  map.on('resize', collapseAttribution);

  // Camera priority, here and in reframe(): a bbox from a real user gesture
  // wins, then an active radius, then the extent of the results, and failing all
  // three the default center/zoom this map was constructed with.
  //
  // Remount (list <-> both/map switch) restores the viewport the list is
  // currently filtered on; without a bbox an active location defines the view
  if (bbox.value) {
    withProgrammaticMove(() => map.fitBounds(bbox.value, { animate: false }));
  } else if (hasRadius()) {
    fitRadius(false);
  } else if (criteria.regions.length) {
    // A region page, or a shared /projecten?regions= URL, opens already narrowed
    // to one part of the country while the default camera still shows all of it.
    // Frame on the results instead, the same move a region change makes once the
    // page is running (WHN-2128). Status and sort do not qualify: those thin out
    // what is in view rather than moving it elsewhere.
    if (results.markers.length) {
      fitToMarkers();
    } else {
      pendingFit = true;
    }
  }

  watch(
    () => [criteria.long, criteria.lat, criteria.radius],
    () => {
      // A location filter defines the view itself, so it takes the camera back
      // from a pending re-frame request.
      pendingFit = false;
      updateRadiusCircle();
      if (hasRadius()) {
        fitRadius(true);
        return;
      }
      // Location or radius switched off: nothing defines the view any more and
      // the results just went national, so re-frame on them once they land
      // (WHN-2128).
      pendingFit = true;
    }
  );

  watch(
    () => results.markers,
    (markers) => {
      const source = map.getSource(PROJECTS_SOURCE);
      if (source) source.setData(toGeoJson(markers));
      // Re-frame only once the fresh markers are in: the request arrives with
      // the filter change, the new result set a debounce later.
      if (pendingFit) {
        pendingFit = false;
        reframe();
      }
    }
  );

  watch(fitRequest, () => {
    pendingFit = true;
  });

  // See ListingMap for why the container resize runs behind the programmatic
  // flag and redraws synchronously.
  resizeObserver = new ResizeObserver(() => {
    programmaticMove = true;
    map.resize();
    programmaticMove = false;
    map.redraw();
    narrowMap.value = container.value.clientWidth < NARROW_MAP_WIDTH;
  });
  resizeObserver.observe(container.value);
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
  tooltip.hide();
  if (map) map.remove();
  map = null;
});
</script>
